import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Gift } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { useCart } from '../context/CartContext';
import { getFreeGiftOffer } from '../lib/api';

const DrawerGiftProgress = ({ subtotal }) => {
  const [offer, setOffer] = useState(null);
  useEffect(() => { getFreeGiftOffer().then((d) => setOffer(d.offer)).catch(() => {}); }, []);
  if (!offer) return null;
  const remaining = offer.minOrder - subtotal;
  const pct = Math.min(100, (subtotal / offer.minOrder) * 100);
  const unlocked = remaining <= 0;
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${unlocked ? 'border-alp-gold/60 bg-alp-gold/[0.07]' : 'border-alp-line bg-alp-dark/40'}`} data-testid="drawer-gift-progress">
      <p className="flex items-start gap-2 text-[11px] text-alp-cream/90 leading-relaxed">
        <Gift size={13} className="text-alp-gold shrink-0 mt-0.5" />
        {unlocked ? (
          <span><span className="text-alp-gold font-semibold">Free gift unlocked!</span> Use code <span className="text-alp-gold font-semibold tracking-wider">{offer.code}</span> at checkout.</span>
        ) : (
          <span>Spend <span className="text-alp-gold font-semibold">${remaining.toFixed(2)}</span> more to unlock a free {offer.product.name}.</span>
        )}
      </p>
      <div className="mt-2 h-1 rounded-full bg-alp-line/50 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-alp-gold/70 to-alp-gold transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function CartDrawer() {
  const navigate = useNavigate();
  const { items, updateQty, removeFromCart, subtotal, shipping, total, freeExpress, drawerOpen, closeDrawer } = useCart();

  const go = (path) => { closeDrawer(); navigate(path); };

  return (
    <Sheet open={drawerOpen} onOpenChange={(o) => !o && closeDrawer()}>
      <SheetContent side="right" data-testid="cart-drawer" className="w-full sm:max-w-md bg-alp-black border-l border-alp-gold/20 text-alp-cream p-0 flex flex-col">
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-alp-line/70">
          <SheetTitle className="font-serif text-2xl text-alp-cream flex items-center gap-2.5">
            <ShoppingBag size={20} className="text-alp-gold" /> Your Cart
            <span className="text-alp-gray text-sm font-normal">({items.reduce((s, i) => s + i.qty, 0)})</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag size={40} strokeWidth={1} className="text-alp-gold/60" />
            <p className="text-alp-gray text-sm">Your cart is empty.</p>
            <button onClick={() => go('/shop')} data-testid="drawer-shop-btn" className="btn-glow inline-flex items-center gap-2 bg-alp-gold text-alp-black px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
              Browse the Collection <ArrowRight size={13} />
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.map((i) => (
                <div key={i.id} data-testid={`drawer-item-${i.id}`} className="flex gap-3.5 border border-alp-line/60 rounded-lg p-3 bg-alp-card/40">
                  {i.image ? <img src={i.image} alt={i.name} className="w-16 h-20 object-cover rounded bg-alp-dark shrink-0" /> : <div className="w-16 h-20 rounded bg-alp-dark shrink-0 flex items-center justify-center text-alp-gold font-serif text-lg">🎁</div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-serif text-alp-cream leading-snug truncate">{i.name}</p>
                        <p className="text-alp-gray text-[11px] mt-0.5">{i.size}{i.stockStatus === 'Preorder' ? ' \u00b7 Preorder' : ''}</p>
                      </div>
                      <button onClick={() => removeFromCart(i.id)} data-testid={`drawer-remove-${i.id}`} aria-label="Remove" className="text-alp-gray hover:text-red-400 transition-colors p-1 shrink-0">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-2.5">
                      <div className="flex items-center border border-alp-line rounded">
                        <button onClick={() => updateQty(i.id, i.qty - 1)} data-testid={`drawer-qty-minus-${i.id}`} className="w-7 h-7 flex items-center justify-center text-alp-cream hover:text-alp-gold transition-colors"><Minus size={12} /></button>
                        <span className="w-8 text-center text-sm" data-testid={`drawer-qty-${i.id}`}>{i.qty}</span>
                        <button onClick={() => updateQty(i.id, i.qty + 1)} data-testid={`drawer-qty-plus-${i.id}`} className="w-7 h-7 flex items-center justify-center text-alp-cream hover:text-alp-gold transition-colors"><Plus size={12} /></button>
                      </div>
                      <span className="text-alp-gold text-sm">${(i.price * i.qty).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-alp-line/70 px-5 py-4 space-y-3 bg-alp-dark/40">
              <DrawerGiftProgress subtotal={subtotal} />
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-alp-gray"><span>Subtotal</span><span className="text-alp-cream" data-testid="drawer-subtotal">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-alp-gray"><span>Shipping</span><span className={freeExpress ? 'text-emerald-300' : 'text-alp-cream'}>{freeExpress ? 'FREE Express' : `$${shipping.toFixed(2)}`}</span></div>
                <div className="flex justify-between text-alp-cream pt-1.5 border-t border-alp-line/60"><span className="tracking-[0.1em] uppercase text-xs pt-0.5">Total</span><span className="text-alp-gold text-lg" data-testid="drawer-total">${total.toFixed(2)} <span className="text-alp-gray text-[10px]">AUD</span></span></div>
              </div>
              <button onClick={() => go('/checkout')} data-testid="drawer-checkout-btn" className="btn-glow w-full inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black py-3.5 text-xs tracking-[0.22em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
                Checkout <ArrowRight size={14} />
              </button>
              <button onClick={() => go('/cart')} data-testid="drawer-view-cart-btn" className="w-full text-center text-[11px] tracking-[0.18em] uppercase text-alp-cream/70 hover:text-alp-gold transition-colors py-1">
                View Full Cart
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
